import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { Link } from 'react-router-dom';
import ButtonElement from '@/components/elements/Button/Button';

const CookieConsentBanner: React.FC = () => {
  const { showBanner, consentToCookies, denyCookies } = useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-white backdrop-blur-sm border-t-2 border-custom-secolor shadow-lg"
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex gap-3">
            {/* Cookie Icon - Fixed position */}
            <div className="flex-shrink-0 mt-0.5">
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Cookie base */}
                <path 
                  d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2Z" 
                  fill="#8B4513"
                  stroke="#654321"
                  strokeWidth="0.5"
                />
                {/* Bite mark */}
                <path 
                  d="M18 6C18 6 17.5 5.5 17 6C16.5 6.5 16.5 7 17 7.5C17.5 8 18 8 18.5 7.5C19 7 19 6.5 18 6Z" 
                  fill="#654321"
                />
                {/* Chocolate chips */}
                <circle cx="7" cy="8" r="1.2" fill="#3E2723"/>
                <circle cx="16" cy="9" r="1.2" fill="#3E2723"/>
                <circle cx="9" cy="15" r="1.2" fill="#3E2723"/>
                <circle cx="15" cy="16" r="1.2" fill="#3E2723"/>
                <circle cx="12" cy="6" r="1.2" fill="#3E2723"/>
                <circle cx="6" cy="12" r="1.2" fill="#3E2723"/>
                <circle cx="18" cy="13" r="1.2" fill="#3E2723"/>
                <circle cx="11" cy="18" r="1.2" fill="#3E2723"/>
                {/* Crumbs near bite */}
                <circle cx="17.5" cy="6.5" r="0.4" fill="#654321"/>
                <circle cx="17.8" cy="7.2" r="0.3" fill="#654321"/>
              </svg>
            </div>
            
            {/* Main Content Area */}
            <div className="flex-1">
              {/* Top row with title and details button */}
              <div className="flex justify-between items-center mb-2">
                <h3 className="content-text-size text-custom-secolor font-semibold">
                  We use cookies to enhance your experience
                </h3>
                
                {/* Toggle Details Button - Top Right */}
                <motion.button
                  onClick={() => setShowDetails(!showDetails)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  className="text-custom-secolor hover:underline text-xs font-medium flex-shrink-0"
                >
                  {/* Mobile: Short version */}
                  <span className="block sm:hidden">
                    {showDetails ? 'Hide' : 'Details'}
                  </span>
                  
                  {/* Small screens and up: Full version */}
                  <span className="hidden sm:block">
                    {showDetails ? 'Hide details' : 'Show details'}
                  </span>
                </motion.button>
              </div>

              {/* Action Buttons - Always side by side */}
              <div className="flex flex-row gap-2 min-w-fit font-size-10">
                <ButtonElement
                  btnText={
                    <>
                      {/* Mobile: Short version */}
                      <span className="block sm:hidden text-xs">
                        Reject
                      </span>
                      
                      {/* Small screens and up: Full version */}
                      <span className="hidden sm:block text-xs">
                        Reject All
                      </span>
                    </>
                  }
                  onClick={denyCookies}
                  size="small"
                  className="px-6 py-1 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-lg"
                  overrideTextColor={true}
                />
                <ButtonElement
                  btnText={
                    <>
                      {/* Mobile: Short version */}
                      <span className="block sm:hidden text-xs">
                        Accept
                      </span>
                      
                      {/* Small screens and up: Full version */}
                      <span className="hidden sm:block text-xs">
                        Accept All
                      </span>
                    </>
                  }
                  onClick={consentToCookies}
                  size="small"
                  className="px-6 py-1 rounded-lg  "
                />
              </div>

              {/* Details Section */}
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mt-3 p-3 bg-gray-50/80 rounded-lg"
                >
                  <h4 className="font-medium text-custom-secolor mb-1 text-sm">Types of cookies we use:</h4>
                  <p className="text-xs text-gray-600 mb-2">
                    We use cookies and similar technologies to help personalize content, 
                    provide a better user experience, and analyze our traffic. 
                    By clicking "Accept All", you consent to our use of cookies.
                  </p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• <strong>Essential cookies:</strong> Required for the website to function properly</li>
                    <li>• <strong>Analytics cookies:</strong> Help us understand how visitors use our website</li>
                    <li>• <strong>Functional cookies:</strong> Remember your preferences and choices</li>
                  </ul>
                  <div className="mt-2">
                    <Link 
                      to="/cookie-policy" 
                      className="text-custom-secolor hover:underline text-xs font-medium"
                    >
                      Read our full Cookie Policy →
                    </Link>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsentBanner; 